import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectApexAccounts } from '@/store/features/apex/apex-selectors';
import { selectPlaidAccounts } from '@/store/features/plaid/plaid-selectors';
import { selectTransferState } from '@/store/features/transfer/transfer-selectors';
import { fetchAccountBankRelationships } from '@/api/custom';
import {
  GetBankRelationshipResponseEntry,
  TransactionInfo,
  IRA_ACCOUNT_TYPES,
} from '@/util/transfer-util';
import { useNavigate } from 'react-router-dom';
import { setTransferState } from '@/store/features/transfer/transferSlice';
import {
  Form,
  Select,
  Button,
  Spin,
  Card,
  InputNumber,
  Alert,
  Modal,
  Space,
  Typography,
  Checkbox,
  Empty,
} from 'antd';
import { useTheme } from '@/contexts/ThemeContext';
import { produce } from 'immer';
import { formatCurrency } from '@wavvest/shared-utils';
import PlaidLinkModal from './PlaidLinkModal';
import { fetchAccountFinancialDetails } from '@/store/features/apex/apex-thunks';
import { useCustodianAccounts } from '@/hooks/useCustodianAccounts';
import { formatAccountType } from '@/util/account-type-format';
import {
  findDepositSourceAccount,
  formatDepositSourceLabel,
  getDepositEligiblePlaidAccounts,
  getPreferredDepositFundingPlaidId,
  isApprovedDepositBankRelationship,
} from './deposit-account-selection';

const { Option } = Select;
const { Title, Text } = Typography;

// Stable empty list so the account-keyed relationships memo below keeps a
// steady identity while a fetch is in flight.
const NO_BANK_RELATIONSHIPS: GetBankRelationshipResponseEntry = [];

const TransferPortfolioToBank: React.FC = () => {
  const { theme } = useTheme();
  const dispatch = useAppDispatch();
  const globalApexAccounts = useAppSelector(selectApexAccounts);
  const globalPlaidAccounts = useAppSelector(selectPlaidAccounts);
  const globalTransferState = useAppSelector(selectTransferState);
  const [plaidModalVisible, setPlaidModalVisible] = useState(false);

  const plaidAccounts = useMemo(
    () => getDepositEligiblePlaidAccounts(globalPlaidAccounts),
    [globalPlaidAccounts],
  );
  // The legacy per-account Apex list — only meaningful (and only used below)
  // for a legacy Apex firm. Always empty for a firm-data + Apex firm, since
  // that pipeline never runs there (see useCustodianAccounts). IRA
  // distribution constraints are only ever available on this list.
  const legacyApexAccounts = useMemo(() => globalApexAccounts, [globalApexAccounts]);
  // Withdrawal-source accounts to actually list/select from: the legacy
  // list above for a legacy Apex firm, or firm-data's blended custodian
  // accounts (real Apex ids via custodian_account_id) for a firm-data +
  // Apex firm.
  const { accounts: resolvedApexAccounts } = useCustodianAccounts();
  const hasLinkedPlaidAccounts = globalPlaidAccounts.length > 0;
  const hasWithdrawalEligiblePlaidAccounts = plaidAccounts.length > 0;
  const navigate = useNavigate();
  const [bankRelationships, setBankRelationships] = useState<GetBankRelationshipResponseEntry>([]);
  // The custodian account the loaded relationships above actually belong to.
  const [bankRelationshipsAccountId, setBankRelationshipsAccountId] = useState<string | undefined>(
    undefined,
  );
  const [bankRelationshipsLoading, setBankRelationshipsLoading] = useState(false);

  // use useRef to track requested accounts, avoid duplicate requests
  const requestedAccountIds = useRef(new Set<string>());

  useEffect(() => {
    legacyApexAccounts.forEach((account) => {
      // check if data is needed and not yet requested
      if (
        (!account.ira_contribution_constraints || !account.ira_distribution_constraints) &&
        !requestedAccountIds.current.has(account.id)
      ) {
        requestedAccountIds.current.add(account.id);
        dispatch(fetchAccountFinancialDetails(account.id));
      }
    });
  }, [legacyApexAccounts, dispatch]);

  const transaction = useMemo(
    () =>
      globalTransferState.state === 'Transferring'
        ? globalTransferState.transactions[0]
        : undefined,
    [globalTransferState],
  );

  useEffect(() => {
    if (
      hasWithdrawalEligiblePlaidAccounts &&
      globalTransferState.state === 'WaitingForFirstAccount'
    ) {
      const fundingPlaidId = getPreferredDepositFundingPlaidId(plaidAccounts, bankRelationships);
      if (!fundingPlaidId) {
        return;
      }
      dispatch(
        setTransferState({
          state: 'Transferring',
          transactions: globalTransferState.transactions.map((transaction) => ({
            ...transaction,
            plaidAccountId: fundingPlaidId,
          })),
        }),
      );
    }
  }, [
    plaidAccounts,
    bankRelationships,
    globalTransferState,
    hasWithdrawalEligiblePlaidAccounts,
    dispatch,
  ]);

  useEffect(() => {
    const accountId = transaction?.custodianAccountId;

    let cancelled = false;

    if (!accountId) {
      setBankRelationships([]);
      setBankRelationshipsAccountId(undefined);
      setBankRelationshipsLoading(false);
      return undefined;
    }

    setBankRelationships([]);
    setBankRelationshipsAccountId(undefined);
    setBankRelationshipsLoading(true);

    (async () => {
      try {
        const response = await fetchAccountBankRelationships(accountId);
        if (cancelled) {
          return;
        }
        setBankRelationships(
          response.bank_relationships.filter(
            (a) => a.state === 'APPROVED' || a.state === 'PENDING',
          ),
        );
        setBankRelationshipsAccountId(accountId);
      } catch (e) {
        if (cancelled) {
          return;
        }
        setBankRelationships([]);
        setBankRelationshipsAccountId(undefined);
        Modal.error({
          title: 'Network Error',
          content: `Unable to fetch bank relationships. Please try again later. Details: ${e}`,
        });
      } finally {
        if (!cancelled) {
          setBankRelationshipsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [transaction?.custodianAccountId]);

  const noWithdrawalAccountCopy = hasLinkedPlaidAccounts
    ? {
        title: 'No Withdrawal-Eligible Bank Accounts',
        description:
          'To make a withdrawal, link a checking or savings account that can be used for withdrawals.',
        button: 'Link Checking or Savings Account',
      }
    : {
        title: 'No External Bank Accounts Connected',
        description: 'To make a withdrawal, you need to connect an external bank account first.',
        button: 'Link External Account',
      };

  // Relationships are only meaningful for the account they were fetched for.
  // Keying them by custodianAccountId keeps a previously selected account's
  // list out of the funding options, the preferred-funding pick and the Review
  // gate for the render between a source change and the new fetch landing.
  const accountBankRelationships = useMemo(
    () =>
      bankRelationshipsAccountId !== undefined &&
      bankRelationshipsAccountId === transaction?.custodianAccountId
        ? bankRelationships
        : NO_BANK_RELATIONSHIPS,
    [bankRelationships, bankRelationshipsAccountId, transaction?.custodianAccountId],
  );

  const preferredFundingPlaidId = useMemo(
    () => getPreferredDepositFundingPlaidId(plaidAccounts, accountBankRelationships),
    [plaidAccounts, accountBankRelationships],
  );

  useEffect(() => {
    if (!transaction || transaction.kind !== 'withdraw' || !preferredFundingPlaidId) {
      return;
    }

    // Wait for this account's own relationships rather than reconciling
    // against an empty list and churning through a throwaway funding id.
    if (bankRelationshipsAccountId !== transaction.custodianAccountId) {
      return;
    }

    const selectedBankRelationship = accountBankRelationships.find(
      (b) => b.plaid_id === transaction.plaidAccountId,
    );

    if (isApprovedDepositBankRelationship(selectedBankRelationship)) {
      return;
    }

    if (transaction.plaidAccountId === preferredFundingPlaidId) {
      return;
    }

    dispatch(
      setTransferState({
        state: 'Transferring',
        transactions: [
          produce(transaction, (t) => {
            t.plaidAccountId = preferredFundingPlaidId;
          }),
        ],
      }),
    );
  }, [
    preferredFundingPlaidId,
    accountBankRelationships,
    bankRelationshipsAccountId,
    dispatch,
    transaction,
  ]);

  const handlePlaidSuccess = () => {
    // Refresh the page or trigger a data refresh
    window.location.reload();
  };

  if (!hasWithdrawalEligiblePlaidAccounts) {
    const containerClass = `transfer-withdrawal-container ${theme}-theme`;

    return (
      <div className={containerClass} style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <Title level={4} style={{ marginBottom: 16 }}>
                    {noWithdrawalAccountCopy.title}
                  </Title>
                  <Text
                    type="secondary"
                    style={{ fontSize: 16, display: 'block', marginBottom: 24 }}
                  >
                    {noWithdrawalAccountCopy.description}
                  </Text>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => setPlaidModalVisible(true)}
                    style={{
                      borderRadius: 8,
                      padding: '8px 24px',
                      height: 'auto',
                    }}
                  >
                    {noWithdrawalAccountCopy.button}
                  </Button>
                </div>
              }
            />
          </div>
        </Card>

        <PlaidLinkModal
          visible={plaidModalVisible}
          onClose={() => setPlaidModalVisible(false)}
          onSuccess={handlePlaidSuccess}
        />
      </div>
    );
  }

  if (!transaction || transaction.kind !== 'withdraw' || transaction.plaidAccountId === undefined) {
    return (
      <div style={{ padding: 24 }}>
        <Alert message="No Data" description="No data available" type="warning" showIcon />
      </div>
    );
  }

  // IRA distribution fields below are only meaningful for the legacy
  // per-account Apex list, which is the only source that carries
  // ira_distribution_constraints. A firm-data-sourced source account
  // simply won't match here, so accountType/requireIra/validIraTypes fall
  // back to their empty defaults rather than guessing at IRA eligibility.
  const apexAccount = legacyApexAccounts.find((a) => a.id === transaction.custodianAccountId);

  const accountType = apexAccount?.type ?? '';
  const requireIra = IRA_ACCOUNT_TYPES.has(accountType);

  const validIraTypes = apexAccount?.ira_distribution_constraints?.valid_types ?? [];

  const currentIraType = validIraTypes.find((t) => t.type === transaction.iraDistributionType);
  const federalTaxEnabled = currentIraType?.federal_withholding_enabled ?? false;
  const stateTaxEnabled = currentIraType?.state_withholding_enabled ?? false;

  // cash_available_for_withdraw only exists on the legacy Apex list. Firm-data
  // destinations do not carry it — do not substitute totalEquity (market value
  // is not withdrawable cash) and do not treat a missing field as $0.
  const parsedCashAvailable = parseFloat(apexAccount?.cash_available_for_withdraw ?? '');
  const hasCashAvailableConstraint = Number.isFinite(parsedCashAvailable);

  const selectedBankRelationship = accountBankRelationships.find(
    (b) => b.plaid_id === transaction.plaidAccountId,
  );
  const selectedBankRelationshipApproved =
    isApprovedDepositBankRelationship(selectedBankRelationship);
  const withdrawalReadinessWarning = selectedBankRelationship
    ? {
        message: 'Bank Account Pending Approval',
        description:
          'This bank account is still being approved for withdrawals. Select an approved bank account or try again later.',
      }
    : {
        message: 'Bank Account Not Ready for Withdrawals',
        description:
          'This linked bank account is not ready for withdrawals. Select an approved checking or savings account or try again later.',
      };

  const handleAmountChange = (value: number | null) => {
    if (value !== null) {
      dispatch(
        setTransferState({
          state: 'Transferring',
          transactions: [
            produce(transaction, (t) => {
              t.amount = value;
            }),
          ],
        }),
      );
    }
  };

  const handleFromAccountChange = (accountId: string) => {
    const selectedAccount = resolvedApexAccounts.find((a) => a.id === accountId);
    if (selectedAccount) {
      dispatch(
        setTransferState({
          state: 'Transferring',
          transactions: [
            produce(transaction, (t) => {
              t.custodianAccountId = accountId;
              if (!IRA_ACCOUNT_TYPES.has(selectedAccount.type)) {
                t.iraDistributionType = undefined;
              }
            }),
          ],
        }),
      );
    }
  };

  const handleToAccountChange = (plaidId: string) => {
    dispatch(
      setTransferState({
        state: 'Transferring',
        transactions: [
          produce(transaction, (t) => {
            t.plaidAccountId = plaidId;
          }),
        ],
      }),
    );
  };

  const handleIraDistributionTypeChange = (type: string) => {
    dispatch(
      setTransferState({
        state: 'Transferring',
        transactions: [
          produce(transaction, (t) => {
            t.iraDistributionType = type as TransactionInfo['iraDistributionType'];
          }),
        ],
      }),
    );
  };

  const handleFederalWithholdChange = (value: number | null) => {
    if (value !== null) {
      dispatch(
        setTransferState({
          state: 'Transferring',
          transactions: [
            produce(transaction, (t) => {
              t.iraDistributionFederalWithhold = value;
            }),
          ],
        }),
      );
    }
  };

  const handleStateWithholdChange = (value: number | null) => {
    if (value !== null) {
      dispatch(
        setTransferState({
          state: 'Transferring',
          transactions: [
            produce(transaction, (t) => {
              t.iraDistributionStateWithhold = value;
            }),
          ],
        }),
      );
    }
  };

  const handleStateWaiverChange = (checked: boolean) => {
    dispatch(
      setTransferState({
        state: 'Transferring',
        transactions: [
          produce(transaction, (t) => {
            t.iraDistributionStateWithholdWaiver = checked;
          }),
        ],
      }),
    );
  };

  const handleCancel = () => {
    navigate('/home/wealth');
  };

  const containerClass = `transfer-withdrawal-container ${theme}-theme`;

  return (
    <div className={containerClass} style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <Spin spinning={bankRelationshipsLoading} size="large">
        <Card title="Transfer from Portfolio to Bank">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Form layout="vertical" size="large">
              {/* Amount Input */}
              <Form.Item label="Amount">
                <InputNumber
                  size="large"
                  style={{ width: '100%' }}
                  value={transaction.amount}
                  onChange={handleAmountChange}
                  formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                  min={0}
                  precision={2}
                />
                {hasCashAvailableConstraint && (
                  <Text type="secondary" style={{ userSelect: 'none' }}>
                    Balance available for withdrawal: {formatCurrency(parsedCashAvailable)}
                  </Text>
                )}
              </Form.Item>

              {/* From Account */}
              <Form.Item label="From">
                <Select
                  value={transaction.custodianAccountId}
                  onChange={handleFromAccountChange}
                  placeholder="Select investment account"
                  size="large"
                >
                  {resolvedApexAccounts.map((account) => (
                    <Option key={account.id} value={account.id}>
                      {`${formatAccountType(account.type)} (${account.name})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* To Account */}
              <Form.Item label="To">
                <Select
                  value={transaction.plaidAccountId}
                  onChange={handleToAccountChange}
                  placeholder="Select bank account"
                  size="large"
                >
                  {accountBankRelationships.map((relationship) => {
                    if (!relationship.plaid_id) {
                      return null;
                    }

                    const sourceAccount = findDepositSourceAccount(
                      plaidAccounts,
                      relationship.plaid_id,
                    );

                    return (
                      <Option key={relationship.plaid_id} value={relationship.plaid_id}>
                        {formatDepositSourceLabel(relationship, sourceAccount)}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>

              {/* IRA Distribution Type */}
              {requireIra && (
                <Form.Item label="IRA Distribution Type">
                  <Select
                    value={transaction.iraDistributionType}
                    onChange={handleIraDistributionTypeChange}
                    placeholder="Select distribution type"
                    size="large"
                  >
                    {validIraTypes.map((type) => (
                      <Option key={type.type} value={type.type}>
                        {(type.type ?? '-').split('_').join(' ')}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              {/* Federal Tax Withholding */}
              {requireIra && federalTaxEnabled && (
                <Form.Item label="Federal Tax Withholding">
                  <InputNumber
                    style={{ width: '100%' }}
                    value={transaction.iraDistributionFederalWithhold ?? 0}
                    onChange={handleFederalWithholdChange}
                    formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                    min={0}
                    precision={2}
                    size="large"
                  />
                </Form.Item>
              )}

              {/* State Tax Withholding */}
              {requireIra && stateTaxEnabled && (
                <>
                  <Form.Item label="State Tax Withholding">
                    <InputNumber
                      style={{ width: '100%' }}
                      value={transaction.iraDistributionStateWithhold ?? 0}
                      onChange={handleStateWithholdChange}
                      formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                      min={0}
                      precision={2}
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item>
                    <Checkbox
                      checked={transaction.iraDistributionStateWithholdWaiver ?? false}
                      onChange={(e) => handleStateWaiverChange(e.target.checked)}
                    >
                      Whether or not this distribution has withholding waiver
                    </Checkbox>
                  </Form.Item>
                </>
              )}

              {/* Warning Message */}
              {/* Until this account's relationships land there is nothing to
                  judge readiness from, so stay quiet rather than blaming the
                  bank account for a selection that has not resolved yet. */}
              {!bankRelationshipsLoading && !selectedBankRelationshipApproved && (
                <Alert
                  message={withdrawalReadinessWarning.message}
                  description={withdrawalReadinessWarning.description}
                  type="warning"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              )}

              {/* Review and Cancel Buttons */}
              <Form.Item>
                <Space size="middle">
                  <Button
                    type="primary"
                    size="large"
                    disabled={
                      transaction.amount <= 0 ||
                      (hasCashAvailableConstraint && transaction.amount > parsedCashAvailable) ||
                      !selectedBankRelationshipApproved
                    }
                    onClick={() => navigate('/home/transfer')}
                  >
                    Review
                  </Button>
                  <Button size="large" onClick={handleCancel}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Space>
        </Card>
      </Spin>
    </div>
  );
};

export default TransferPortfolioToBank;
